import axios from "axios";
import { reasonFromConnectionError, REQUEST_CUSTOM_PLAN_EVENT } from '@/lib/utils/customPlanReasons';
import { queryClient } from "../query";
import { AccessControlStore } from "./accessControl";

// In dev, call the Vite proxy so the browser stays on localhost.
// The live API sends Cross-Origin-Resource-Policy: same-origin, which
// Firefox blocks even when Access-Control-Allow-Origin allows this app.
export const API_URL = import.meta.env.DEV
  ? '/api'
  : import.meta.env.VITE_API_BASE_URL || 'https://backend.sellco.ai';

export const apiClient = axios.create({
  baseURL: API_URL,
  // timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request Interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRedirecting = false;

export const resetRedirectLatch = () => {
  isRedirecting = false;
};

// Response Interceptor
apiClient.interceptors.response.use(
  (response) => {
    isRedirecting = false;
    return response;
  },
  (error) => {
    if (error.response?.status === 403 && error.response.data?.error === 'CONNECTION_LIMIT_REACHED') {
      import('antd').then(({ notification, Button, Space }) => {
        import('react').then((React) => {
          const matchingReason = reasonFromConnectionError(error.response.data);

          notification.error({
            message: 'Connection Limit Reached',
            description: error.response.data.message,
            duration: 0,
            btn: React.createElement(
              Space,
              { size: 'small' },
              React.createElement(
                Button,
                {
                  size: 'small',
                  onClick: () => {
                    notification.destroy();
                    window.location.href = '/dashboard/profile/billing';
                  },
                },
                'Upgrade plan',
              ),
              React.createElement(
                Button,
                {
                  type: 'primary',
                  size: 'small',
                  onClick: () => {
                    notification.destroy();
                    // A page that shows the modal itself marks the event handled; otherwise go to Billing.
                    const detail = { reason: matchingReason, handled: false };
                    window.dispatchEvent(new CustomEvent(REQUEST_CUSTOM_PLAN_EVENT, { detail }));
                    if (!detail.handled) {
                      window.location.href = `/dashboard/profile/billing?requestCustom=1&reason=${matchingReason}`;
                    }
                  },
                },
                'Request Custom plan',
              ),
            ),
          });
        });
      });
      return Promise.reject(error);
    }

    const gateError = error.response?.data?.error;
    const isSubscriptionDenial =
      gateError?.code === 'SUBSCRIPTION_EXPIRED' ||
      gateError?.code === 'SUBSCRIPTION_SUSPENDED';

    if (isSubscriptionDenial) {
      queryClient.invalidateQueries({ queryKey: ['active-subscription'] });

      const targetPath = gateError?.state === 'SUSPENDED'
        ? (gateError?.infoPath || '/suspended')
        : (gateError?.infoPath || gateError?.renewPath || '/dashboard/profile/billing');

      const currentPath = window.location.pathname;

      if (
        currentPath !== targetPath &&
        currentPath !== '/suspended' &&
        currentPath !== '/no-access'
      ) {
        if (!isRedirecting) {
          isRedirecting = true;
          AccessControlStore.setDeniedContext(gateError);
          window.location.href = targetPath;
        }
      }
    } else if (
      error.response?.status === 403 &&
      error.response.data?.error?.code === "FEATURE_NOT_IN_PLAN"
    ) {
      queryClient.invalidateQueries({ queryKey: ['active-subscription'] });
      
      const currentPath = window.location.pathname;
      const featureRoute = error.response.data.error.feature?.route?.replace('/*', '');
      const isCurrentRoute = featureRoute ? currentPath.startsWith(featureRoute) : true;

      if (isCurrentRoute && currentPath !== '/no-access' && currentPath !== '/dashboard/profile/billing') {
        if (!isRedirecting) {
          isRedirecting = true;
          AccessControlStore.setDeniedContext(error.response.data.error);
          window.location.href = '/no-access';
        }
      }
    } else if (error.response?.status === 401) {
      // Handle unauthorized access
      console.error("Unauthorized! Redirecting to login...");
      // You could dispatch an action to clear auth state and redirect
    }
    return Promise.reject(error);
  }
);
